import z from 'zod'
import type { OptionSelect } from './components/ElementsUi'

const baseUrl = '/api/'

export const apiRoutes = {
    checkProfile: (id: string) => `/customer/registration/${id}`,
    registerSubmit: (id: string) => `/customer/registration/${id}`,
    secretCheck: '/excelImport/check-auth',
    getUploadHistory: (filter?: string) => `/excelImport/history/po${filter}`,
}

export const apiHandler = <T>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    body?: any,
    headers?: any
) =>
    new Promise<T>((resolve, reject) => {
        const usedPath = path.startsWith('/') ? path.slice(1) : path
        const uri = baseUrl + usedPath
        const haveBody = !!body && ['POST', 'PATCH', 'PUT'].includes(method)
        fetch(uri, {
            method: method,
            headers: {
                ...(haveBody && { 'Content-type': 'application/json' }),
                ...headers,
            },
            body: haveBody ? JSON.stringify(body) : null,
        })
            .then(res => res.json())
            .then(data => {
                resolve(data as T)
            })
            .catch(error => {
                reject(error)
            })
    })

export enum appStatus {
    init = 0,
    loading = 1,
    invalid = 2,
    customerInput = 3,
    used = 4,
    expired = 5,
    completed = 6,
    error = 7,
}

export const appErrorMapping = {
    server_error: appStatus.error,
    bad_request: appStatus.invalid,
    unauthenticated: appStatus.error,
    unauthorized: appStatus.error,
    not_exist: appStatus.invalid,
    pre_exist: appStatus.used,
    resource_expired: appStatus.expired,
    parallel_requests: appStatus.invalid,
}

export enum errorMessage {
    failed = 'Maaf, terjadi kesalahan saat memproses permintaan Anda. Silakan coba beberapa saat lagi',
    badRequest = 'Permintaan tidak valid. Silakan periksa kembali data yang Anda kirimkan dan coba lagi.',
    internalServerError = 'Terjadi kesalahan di server. Silakan coba lagi nanti.',
    formUsed = 'Maaf Form telah digunakan.',
    error = 'Mohon Periksa koneksi internet anda.',
    invalid = 'silahkan periksa tautan anda dengan benar.',
    expired = 'Maaf link form sudah kadaluarsa',
    notFound = 'data yang diminta tidak ditemukan',
    errGetDataProv = 'Maaf terjadi kesalahan/data provinsi tidak tersedia',
    errGetDataCity = 'Maaf terjadi kesalahan/data Kota/Kabupaten tidak tersedia',
}

// Reponse Wrapper
type SuccessResponseWrapper<T> = {
    error: false
    data: T
}
type ErrorResponseWrapper<C, T = undefined> = {
    error: true
    code: C
    data?: T
    message: string
}

//GET BASE_URL/checkFormId?id=${formId}
type SuccessResProfileheck = SuccessResponseWrapper<{ phone: string }>
type ErrResProfileCheck = ErrorResponseWrapper<keyof typeof appErrorMapping>
export type ResProfileCheck = SuccessResProfileheck | ErrResProfileCheck

type SuccessRegRes = SuccessResponseWrapper<true>
type ErrRegRes = ErrorResponseWrapper<keyof typeof appErrorMapping>
export type RegisterRes = SuccessRegRes | ErrRegRes

const businessKindMap = {
    Retail: 'Retail',
    DistributorPerusahaan: 'Distributor / Perusahaan (PT/CV)',
    Katering: 'Katering',
    PengolahanMakanan: 'Industri Pengolahan Makanan (Rumahan/Pabrik)',
    PedagangPasar: 'Pedagang pasar',
    WarungOnline: 'Warung Online',
    Restoran: 'Restoran / Rumah Makan',
    Other: 'Lainnya (Isi Bebas - Free Text)',
}
const businessKindChoices = Object.freeze(Object.keys(businessKindMap))
export const BusinessKindOptions: OptionSelect[] = Object.entries(businessKindMap).map(
    ([key, value]) => ({ label: value, value: key })
)
const jobPosition = {
    PemilikUsaha: 'Pemilik Usaha',
    Pegawai: 'Pegawai',
    KeluargaMembantuUsaha: 'Anggota Keluarga yang Membantu Usaha',
    Kurir: 'Kurir',
    Other: 'Lainnya (Isi Bebas - Free Text)',
}
const jobPositionChoices = Object.freeze(Object.keys(jobPosition))
export const JobPositionOptions: OptionSelect[] = Object.entries(jobPosition).map(
    ([key, value]) => ({ label: value, value: key })
)
export const SubmissionReqBodyValidator = z.object({
    fullName: z.string().min(3),
    address: z.string().min(3),
    businessKind: z.enum(businessKindChoices as [string, ...string[]]),
    businessKindOther: z.string().optional(),
    jobPosition: z.enum(jobPositionChoices as [string, ...string[]]),
    jobPositionOther: z.string().optional(),
    birthPlace: z.string().min(3),
    birthDate: z.string().date(),
    email: z
        .string()
        .transform(val => (val.trim() === '' ? undefined : val))
        .optional()
        .refine(val => !val || z.string().email().safeParse(val).success, {
            message: 'Format email tidak valid',
        }),
    social_instagram: z.string().optional(),
    social_facebook: z.string().optional(),
    social_tiktok: z.string().optional(),
    consent: z.boolean().optional(),
})
export type SubmissionReqBodyType = z.infer<typeof SubmissionReqBodyValidator>

/**
 * ============================================================================
 * DOKUMENTASI API - EXCEL IMPORT
 * ============================================================================
 *
 * File ini berisi Type dan Interface TypeScript untuk keperluan integrasi
 * API Excel Import di frontend. Tim frontend dapat langsung menggunakan
 * interface di bawah ini untuk *type-safety* saat memanggil API.
 */

// ----------------------------------------------------------------------------
// 1. CHECK AUTH (LOGIN)
// ----------------------------------------------------------------------------
// Endpoint : GET /api/excelImport/check-auth
// Headers  : { "apikey": "<secret_key>" }
// Kegunaan : Memvalidasi apakah secret key (API key) yang diinput valid.

/**
 * Bentuk response dari API Check Auth.
 */
export type CheckAuthRes =
    | SuccessResponseWrapper<{
          /** Bernilai true jika secret key valid */
          success: boolean
          /** Pesan sukses (contoh: "Authentication successful") */
          message: string
      }>
    | ErrorResponseWrapper<keyof typeof appErrorMapping>
// ----------------------------------------------------------------------------
// 2. UPLOAD HISTORY (PURCHASE ORDER)
// ----------------------------------------------------------------------------
// Endpoint : GET /api/excelImport/history/po
// Headers  : { "apikey": "<secret_key>" }
// Query    :
//   - page   : number (opsional, default: 1)
//   - limit  : number (opsional, default: 10, maksimal 100)
//   - search : string (opsional, pencarian berdasarkan nama file Excel)
// Kegunaan : Mengambil riwayat pengunggahan file Excel untuk Purchase Order.

/**
 * Representasi 1 baris riwayat / history dari file Excel yang diupload.
 */
export interface PoExcelHistoryItem {
    /** ID unik histori */
    id: number

    /** URL file di Google Cloud Storage (nama asli file ada di ujung URL ini) */
    fileUrl: string

    /** Waktu ketika file diupload ke sistem (ISO 8601 Date string) */
    createTs: string

    /** Waktu saat sistem mulai memproses isi file Excel (ISO 8601 Date string) */
    startTs: string | null

    /** Waktu saat pemrosesan file Excel selesai (ISO 8601 Date string) */
    doneTs: string | null

    /** Jumlah baris data di Excel yang berhasil masuk ke database */
    rowCountSuccess: number

    /** Jumlah baris data di Excel yang gagal masuk (misal: duplikat) */
    rowCountFail: number

    /** Jumlah baris data transaksi yang dikenali sebagai "Member" */
    rowCountMember: number

    /** Total jumlah Struk / Purchase Order unik di dalam file tersebut */
    poCount: number

    /** Total jumlah Struk / Purchase Order yang berstatus "Member" */
    poCountMember: number
}

/**
 * Informasi meta data untuk keperluan Pagination.
 */

export interface PaginationMeta {
    /** Halaman aktif saat ini */
    page: number

    /** Jumlah data yang ditampilkan per halaman */
    limit: number

    /** Total keseluruhan data yang tersedia di database (tanpa limit) */
    total: number

    /** Total seluruh halaman yang tersedia */
    totalPages: number
}

/**
 * Bentuk response utama dari API History Purchase Order.
 */
export interface PoExcelHistoryResItem {
    /** Array yang berisi daftar histori upload Excel */
    data: PoExcelHistoryItem[]

    /** Detail informasi halaman (pagination) */
    meta: PaginationMeta
}

export type PoExcelHistoryResponse =
    | SuccessResponseWrapper<PoExcelHistoryResItem>
    | ErrorResponseWrapper<keyof typeof appErrorMapping>
